import React, { useState } from "react";
import Captcha from "../../components/CaptchaComponent";
import { useNavigate } from "react-router-dom";

const openGmail = () => {
  window.open("https://mail.google.com", "_blank");
};

export default function LoginForm() {
  const [invalid, setInvalid] = useState(false);
  const [data, setData] = useState({
    email: "",
    password: "",
  });
  // State for showing modal when status is 424
  const [showVerifyEmailModal, setShowVerifyEmailModal] = useState(false);
  const [showOpenMail, setShowOpenMail] = useState(false);
  const [captchaValid, setCaptchaValid] = useState(false); // Track captcha validation

  const navigate = useNavigate();

  const handleChange = (e) => {
    if (invalid) {
      setInvalid(false);
    }
    const { name, value } = e.target;
    setData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleCaptchaSolve = (isValid) => {
    setCaptchaValid(isValid); // Update captcha validation state
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!captchaValid) {
      alert("Please solve the captcha correctly!");
      return;
    }
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_BASE_URL}/api/users/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        }
      );

      if (
        response.status === 400 ||
        response.status === 500 ||
        response.status === 401
      ) {
        setInvalid(true);
      }

      if (response.status === 424) {
        setShowVerifyEmailModal(true); // Show custom modal for verification
      }
      if (response.status === 428) {
        const responseData = await response.json();
        navigate("/reset-password", { state: { email: data.email, token: responseData.onetimeToken } });
      }

      if (response.status === 429) {
        alert("Too many requests, try again after some time");
      }
      if (response.ok) {
        const responseData = await response.json();
        const token = JSON.stringify(responseData);
        localStorage.setItem("token", token);
        if (
          responseData.role === "STUDENT" ||
          responseData.role === "ALUMNI" ||
          responseData.role === "STAFF" ||
          responseData.role === "INDUSTRY"
        ) {
          navigate("/dashboard/");
        }

        if (
          responseData.role === "ADMIN" ||
          responseData.role === "SUPER_USER"
        ) {
          navigate("/admin/");
        }

        if (responseData.role === "NODAL_OFFICER") {
          navigate("/college-admin");
        }

        if (responseData.role === "ADMISSION_OFFICER") {
          navigate("/admission-officer");
        }
      } else {
        console.error("Error logging in:", response.statusText);
      }
    } catch (error) {
      console.error("Error logging in:", error);
    }
  };

  const handleResendMail = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_BASE_URL}/api/users/resend-verification`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email: data.email }),
        }
      );
      setShowVerifyEmailModal(false);
      if (response.ok) {
        setShowOpenMail(true);
      } else {
        alert("Error resending email.");
      }
    } catch (error) {
      console.error("Error resending email:", error);
    }
  };

  return (
    <div className="bg-transparent rounded-3xl">
      <div className="w-80% max-w-md md:max-w-lg p-6 md:p-8 flex flex-col justify-center items-center bg-white rounded-3xl drop-shadow-2xl">
        <h1 className="text-3xl font-custom font-semibold p-2 text-skin-base">
          Welcome Back
        </h1>
        <p className="font-medium text-lg font-custom text-gray-400 p-2 text-center">
          Welcome back! Please enter your details.
        </p>
        <div className="mt-8 w-full">
          <div className="w-full">
            <label className="text-lg font-medium font-custom text-skin-base">
              Email
            </label>
            <input
              className="w-full border-b border-blue-300 p-2 bg-transparent font-custom mb-4 text-skin-base"
              placeholder="Enter your email"
              id="email"
              name="email"
              value={data.email}
              onChange={handleChange}
              required
            />
          </div>
          <div>
            <label className="text-lg font-medium font-custom text-skin-base">
              Password
            </label>
            <input
              className="w-full border-b border-blue-300 p-2 bg-transparent font-custom text-skin-base"
              placeholder="Enter your password"
              type="password"
              id="password"
              name="password"
              value={data.password}
              onChange={handleChange}
              required
            />
          </div>

          <div className="text-right mt-2">
            <a
              href="forgot-password"
              className="text-skin-inverted font-medium hover:underline font-custom"
            >
              Forgot Password?
            </a>
          </div>

          {invalid && (
            <p className="text-center font-custom text-red-600 font-semibold mt-2">
              **Invalid email or password**
            </p>
          )}

          <Captcha onCaptchaSolve={handleCaptchaSolve} />

          <div className="mt-8 flex flex-col gap-y-4">
            <button
              className="flex p-2 rounded-xl font-custom items-center justify-center bg-skin-button text-white gap-2 active:scale-[0.98] active:duration-75 hover:scale-[1.01] ease-in-out transition-all"
              onClick={handleSubmit}
            >
              Login
            </button>
          </div>
        </div>
      </div>

      {/* Custom Modal for Resend Verification */}
      {showVerifyEmailModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-xl p-4 sm:p-6 text-center font-custom w-11/12 sm:w-3/4  lg:w-2/3 xl:w-full max-w-md mx-auto">
            <h2 className="text-xl sm:text-2xl text-skin-inverted font-semibold mb-4">
              Verify Your Email
            </h2>
            <p className="mb-4 text-gray-700">
              Please verify your email before logging in.
            </p>
            <div className="flex flex-row sm:flex-row justify-center gap-4 sm:gap-8">
              <button
                className="w-full sm:w-auto px-4 py-2 bg-skin-button text-white rounded-md hover:bg-skin-button-hover font-custom"
                onClick={handleResendMail}
              >
                Resend Email
              </button>
              <button
                className="w-full sm:w-auto px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-700 font-custom"
                onClick={() => setShowVerifyEmailModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {showOpenMail && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-xl p-4 sm:p-6 text-center font-custom w-11/12 sm:w-3/4 lg:w-2/3 xl:w-full max-w-md mx-auto">
            <h4 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">
              Verification email has been resent.
            </h4>
            <div className="flex flex-row justify-center gap-4 sm:gap-8">
              <button
                className="w-full sm:w-auto px-4 py-2 bg-skin-button text-white rounded-md hover:bg-skin-button-hover font-custom"
                onClick={() => {
                  openGmail();
                  setShowOpenMail(false);
                }}
              >
                Open Gmail
              </button>
              <button
                className="w-full sm:w-auto px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-700 font-custom"
                onClick={() => setShowOpenMail(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
